import { useState, type ReactNode } from "react";
import { resolveUnitImage } from "../../Layouts/lite/components/Sidebar/unitImages";
import { fleetIconImage, useIconsList, type FleetIcon } from "../../features/icons/queries";
import { cn } from "../ui/cn";

export type UnitIconField = string | Pick<FleetIcon, "car" | "states"> | null;

export interface UnitIconSource {
  icon?: UnitIconField;
  type?: string | null;
  model?: string | null;
  brand?: string | null;
}

const iconFieldUrl = (icon?: UnitIconField): string | null => {
  if (!icon) return null;
  if (typeof icon === "string") return icon.startsWith("http") ? icon : null;
  return fleetIconImage(icon as FleetIcon);
};

export const resolveUnitIcon = (
  { icon, type, model, brand }: UnitIconSource,
  photoFallback = false,
): string | null =>
  iconFieldUrl(icon) ??
  (photoFallback
    ? resolveUnitImage(type ?? undefined, model ?? undefined, brand ?? undefined)
    : null);

export function useCatalogIconUrl(iconId?: string | null): string | null {
  const { data } = useIconsList({ enabled: Boolean(iconId) });
  if (!iconId) return null;
  const icon = data?.find((candidate) => candidate.id === iconId);
  return icon ? fleetIconImage(icon) : null;
}

export interface UnitIconProps extends UnitIconSource {
  iconId?: string | null;
  name?: string;
  className?: string;
  photoFallback?: boolean;
  fallback?: ReactNode;
}

/** URL a pintar para una unidad, o `null` si hay que caer al respaldo. */
export function useResolvedUnitIcon(
  { iconId, icon, type, model, brand }: UnitIconSource & { iconId?: string | null },
  photoFallback = false,
): string | null {
  const catalogUrl = useCatalogIconUrl(iconId);
  return catalogUrl ?? resolveUnitIcon({ icon, type, model, brand }, photoFallback);
}

export default function UnitIcon({
  icon,
  iconId,
  type,
  model,
  brand,
  name,
  className,
  photoFallback = false,
  fallback = <i className="ri-truck-line" />,
}: UnitIconProps) {
  const [failed, setFailed] = useState(false);
  const src = useResolvedUnitIcon({ iconId, icon, type, model, brand }, photoFallback);

  if (!src || failed) return <>{fallback}</>;

  return (
    <img
      src={src}
      alt={name ?? ""}
      onError={() => setFailed(true)}
      className={cn("object-cover", className)}
    />
  );
}
